import { Link } from "react-router-dom";
import clsx from "clsx";

import { LinkTarget } from "../../utilities/helpers/linkTarget";
import { useAccessibleTarget } from "./useAccessibleTarget";

/** Plain HTML attributes only: the inert branch is a div, so router-only props have nowhere to go. */
type AccessLinkProps = React.HTMLAttributes<HTMLElement> & {
  /** Null when there is nothing to point at, which renders the same inert content as no access. */
  target: LinkTarget | null;
  /** Classes for the live link only, e.g. a hover affordance an inert row must not show. */
  linkedClassName?: string;
  /** Rendered after the content on the live link only, e.g. an icon saying it opens a page. */
  linkedAdornment?: React.ReactNode;
  newTab?: boolean;
};

/**
 * A row/tile that links to `target` when the user may follow it. Otherwise the same content renders
 * inert: no navigation, no hover affordance, and no hint that a link was withheld, so a card reads
 * identically whatever a user can reach.
 */
export const AccessLink: React.FC<AccessLinkProps> = ({
  target,
  className,
  linkedClassName,
  linkedAdornment,
  newTab,
  children,
  ...rest
}) => {
  const accessible = useAccessibleTarget(target);

  if (!accessible) {
    return <div className={className} {...rest}>{children}</div>;
  }

  return (
    <Link to={accessible.to} target={newTab ? "_blank" : undefined} className={clsx(className, linkedClassName)} {...rest}>
      {children}
      {linkedAdornment}
    </Link>
  );
};
